import scriptjs from 'scriptjs';
import {autobind} from 'core-decorators';
import {getDOMNode, getDOMNodes} from 'utils'; // eslint-disable-line no-unused-vars
import BaseAdapter from './base';
import BrightcoveAdapter from './brightcove';

export const loadScript = scriptjs;

// http://docs.videojs.com/docs/api/player.html
@autobind
export default class VideoJSAdapter extends BaseAdapter {
    static findVideos() {
        const nodes = getDOMNodes('video.video-js, video.vjs-tech');
        return nodes
            .filter(node => BrightcoveAdapter.DATA_ATTRS.every(attr => !node.getAttribute(attr)))
            .filter(node => Boolean(node.getAttribute('id')))
            .map(node => ({
                provider: 'videojs',
                videoId: node.getAttribute('id').replace(/_html5_api$/, ''), // Remove suffix added by videojs
                videoNode: node,
            }));
    }

    getVideoDimensions() {
        const width = this.videoNode.getAttribute('width');
        const height = this.videoNode.getAttribute('height');
        if (width && height) return [parseInt(width, 10), parseInt(height, 10)];
        return super.getVideoDimensions();
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            const apiURL = '//vjs.zencdn.net/5.16.0/video.js';
            const check = () => {
                if (window.videojs && this.videoNode.classList.contains('vjs-tech')) {
                    this.videoNode = this.videoNode.parentNode;
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };

            if (!getDOMNode(`script[src="${apiURL}"]`)) exports.loadScript(apiURL, check);
            else check();

            const timeout = () => reject('VideoJS API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        return new Promise((resolve, reject) => {
            this.enforcePlayerParameters();

            this.api = window.videojs(this.videoNode.getAttribute('id'));
            this.api.on('pause', () => {
                this.setPlayerState(BaseAdapter.STATES.Paused);
                clearInterval(this.syncInterval); // Stop syncing when paused
            });
            this.api.on('play', () => {
                this.setPlayerState(BaseAdapter.STATES.Playing);
                // Start syncing only when the video is playing
                this.syncInterval = setInterval(() => {
                    this.syncPlayhead(); // Call syncPlayhead manually every 75ms
                }, 75);
            });
            this.api.on('seeked', () => {
                this.setPlayerState(BaseAdapter.STATES.Seek);
                this.syncPlayhead(); // Call syncPlayhead manually after seeking
            });
            this.api.on('ended', () => {
                this.setPlayerState(BaseAdapter.STATES.Ended);
                clearInterval(this.syncInterval); // Stop syncing when video ends
            });

            const check = () => {
                if (this.getDuration() !== 0) {
                    this.handleReady();
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();

            const timeout = () => reject('VideoJS Player initialization failed');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    enforcePlayerParameters() {
        const video = this.videoNode.getElementsByTagName('video')[0];
        if (video) {
            video.setAttribute('webkit-playsinline', true);
            video.setAttribute('playsinline', true);
        }
    }

    disableCaptions() {
        const tracks = this.api.textTracks();
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = 'hidden';
        }
    }

    handleReady() {
        this.setDuration(this.getDuration());
        this.disableCaptions();
    }

    syncPlayhead() {
        console.log("Syncing playhead. Time: " + this.getCurrentTime());
        this.setPlayhead(this.getCurrentTime());
    }

    seek(time) {
        this.api.currentTime(this.toSS(time));
    }

    pause() {
        this.api.pause();
    }

    isPlaying() {
        return !this.api.paused();
    }

    getDuration() {
        return this.toMS(this.api.duration());
    }

    getCurrentTime() {
        return this.toMS(this.api.currentTime());
    }

    getVideoTitle() {
        return new Promise(resolve => resolve(''));
    }
}



// WEBPACK FOOTER //
// ./scripts/adapters/videojs.js